import { inject, Injectable, Signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { map, tap } from 'rxjs/operators';

import {
  Metric,
  PageRequest,
  User,
  UserAccount,
  UserCreateUpdateRequest,
  UserStatistics,
  UserUpdateRequest,
} from '../models';
import { AuthStore, DemoStore } from '../stores';
import { Role } from '../enums';
import { DemoDataService } from './demo-data.service';

@Injectable({ providedIn: 'root' })
export class UserService {
  private readonly httpClient = inject(HttpClient);
  private readonly authStore = inject(AuthStore);
  private readonly demoStore = inject(DemoStore);
  private readonly demoDataService = inject(DemoDataService);
  $user = this.authStore.user;

  getUsersStatisticsByRole(): Observable<Metric[]> {
    if (this.demoStore.isDemoMode()) {
      const users = this.demoDataService.getUsers();
      const activeCount = users.filter((u) => u.active).length;
      return of([
        { label: 'users.totalUsers', value: users.length, icon: 'groups', iconSize: 1.5 },
        { label: 'users.activeUsers', value: activeCount, icon: 'person', iconSize: 1.5 },
        { label: 'users.inactiveUsers', value: users.length - activeCount, icon: 'person_off', iconSize: 1.5 },
      ]);
    }
    switch (this.$user()?.role) {
      case Role.GlobalAdmin:
        return this.getAdminUsersStatistics();
      case Role.Partner:
        return this.getPartnerUsersStatistics();
      default:
        return this.getCustomerUsersStatistics();
    }
  }

  getCustomerUsersStatistics(customerId?: number): Observable<Metric[]> {
    if (this.demoStore.isDemoMode()) {
      return this.getUsersStatisticsByRole();
    }
    return this.httpClient
      .get<UserStatistics>(`/api/customers/${customerId || 'me'}/users/statistics`)
      .pipe(map((statistics) => UserStatistics.mapToMetrics(statistics)));
  }

  getPartnerUsersStatistics(partnerId?: string, role?: Role): Observable<Metric[]> {
    if (this.demoStore.isDemoMode()) {
      return this.getUsersStatisticsByRole();
    }
    const params: any = {};
    if (role) {
      params.role = Role.mapToApiValue(role);
    }
    return this.httpClient
      .get<UserStatistics>(`/api/partners/${partnerId || 'me'}/users/statistics`, { params })
      .pipe(map((statistics) => UserStatistics.mapToMetrics(statistics)));
  }

  getAdminUsersStatistics(): Observable<Metric[]> {
    if (this.demoStore.isDemoMode()) {
      return this.getUsersStatisticsByRole();
    }
    return this.httpClient
      .get<UserStatistics>('/api/admin/users/statistics')
      .pipe(map((statistics) => UserStatistics.mapToMetrics(statistics)));
  }

  getUsersByRole(pageRequest: PageRequest): Observable<User[]> {
    if (this.demoStore.isDemoMode()) {
      return of(this.demoDataService.getUsers());
    }
    switch (this.$user()?.role) {
      case Role.GlobalAdmin:
        return this.getAdminUsers(pageRequest);
      case Role.Partner:
        return this.getPartnerUsers(undefined, pageRequest);
      default:
        return this.getCustomerUsers(undefined, pageRequest);
    }
  }

  getCustomerUsers(customerId: number | undefined, pageRequest: PageRequest): Observable<User[]> {
    if (this.demoStore.isDemoMode()) {
      return of(this.demoDataService.getUsers());
    }
    return this.httpClient
      .get<any[]>(`/api/customers/${customerId || 'me'}/users`, { params: PageRequest.mapToQuery(pageRequest) })
      .pipe(map((users) => users.map((user) => User.mapFromApiValue(user))));
  }

  getPartnerUsers(partnerId: string | undefined, pageRequest: PageRequest & { role?: Role }): Observable<User[]> {
    if (this.demoStore.isDemoMode()) {
      return of(this.demoDataService.getUsers());
    }

    let params = {
      ...PageRequest.mapToQuery(pageRequest),
      ...(pageRequest.role && { role: Role.mapToApiValue(pageRequest.role) }),
    };

    return this.httpClient
      .get<any[]>(`/api/partners/${partnerId || 'me'}/users`, { params })
      .pipe(map((users) => users.map((user) => User.mapFromApiValue(user))));
  }

  getAdminUsers(pageRequest: PageRequest): Observable<User[]> {
    if (this.demoStore.isDemoMode()) {
      return of(this.demoDataService.getUsers());
    }
    return this.httpClient
      .get<any[]>('/api/admin/users', { params: PageRequest.mapToQuery(pageRequest) })
      .pipe(map((users) => users.map((user) => User.mapFromApiValue(user))));
  }

  createUserAsUser(request: UserCreateUpdateRequest): Observable<User> {
    if (this.demoStore.isDemoMode()) {
      return of(this.demoDataService.getUsers()[0]);
    }
    return this.httpClient
      .post<any>(`/api/customers/${request.customerId || 'me'}/users`, UserCreateUpdateRequest.mapToApiValue(request))
      .pipe(map((user) => User.mapFromApiValue(user)));
  }

  createUserAsPartner(request: UserCreateUpdateRequest): Observable<User> {
    if (this.demoStore.isDemoMode()) {
      return of(this.demoDataService.getUsers()[0]);
    }
    return this.httpClient
      .post<any>(`/api/partners/${request.partnerId || 'me'}/users`, UserCreateUpdateRequest.mapToApiValue(request))
      .pipe(map((user) => User.mapFromApiValue(user)));
  }

  createUserAsAdmin(request: UserCreateUpdateRequest): Observable<User> {
    if (this.demoStore.isDemoMode()) {
      return of(this.demoDataService.getUsers()[0]);
    }
    return this.httpClient
      .post<any>('/api/admin/users', UserCreateUpdateRequest.mapToApiValue(request))
      .pipe(map((user) => User.mapFromApiValue(user)));
  }

  updateUserAsUser(id: number, request: UserCreateUpdateRequest): Observable<User> {
    if (this.demoStore.isDemoMode()) {
      return of(this.demoDataService.getUsers()[0]);
    }
    return this.httpClient
      .patch<any>(
        `/api/customers/${request.customerId || 'me'}/users/${id}`,
        UserCreateUpdateRequest.mapToApiValue(request),
      )
      .pipe(map((user) => User.mapFromApiValue(user)));
  }

  updateUserAsPartner(id: number, request: UserCreateUpdateRequest): Observable<User> {
    if (this.demoStore.isDemoMode()) {
      return of(this.demoDataService.getUsers()[0]);
    }
    return this.httpClient
      .patch<any>(
        `/api/partners/${request.partnerId || 'me'}/users/${id}`,
        UserCreateUpdateRequest.mapToApiValue(request),
      )
      .pipe(map((user) => User.mapFromApiValue(user)));
  }

  updateUserAsAdmin(id: number, request: UserCreateUpdateRequest): Observable<User> {
    if (this.demoStore.isDemoMode()) {
      return of(this.demoDataService.getUsers()[0]);
    }
    return this.httpClient
      .patch<any>(`/api/admin/users/${id}`, UserCreateUpdateRequest.mapToApiValue(request))
      .pipe(map((user) => User.mapFromApiValue(user)));
  }

  deleteUserByRole(id: number): Observable<void> {
    if (this.demoStore.isDemoMode()) {
      return of();
    }
    switch (this.$user()?.role) {
      case Role.GlobalAdmin:
        return this.deleteAdminUser(id);
      case Role.Partner:
        return this.deletePartnerUser(id, this.$user()?.partner?.id);
      default:
        return this.deleteCustomerUser(id, this.$user()?.customer?.id);
    }
  }

  deleteCustomerUser(id: number, customerId?: number): Observable<void> {
    if (this.demoStore.isDemoMode()) {
      return of();
    }
    return this.httpClient.delete<void>(`/api/customers/${customerId || 'me'}/users/${id}`);
  }

  deletePartnerUser(id: number, partnerId?: string): Observable<void> {
    if (this.demoStore.isDemoMode()) {
      return of();
    }
    return this.httpClient.delete<void>(`/api/partners/${partnerId || 'me'}/users/${id}`);
  }

  deleteAdminUser(id: number): Observable<void> {
    if (this.demoStore.isDemoMode()) {
      return of();
    }
    return this.httpClient.delete<void>(`/api/admin/users/${id}`);
  }

  updateCurrentUser(userUpdateRequest: UserUpdateRequest): Observable<any> {
    if (this.demoStore.isDemoMode()) {
      return of(this.$user()).pipe(
        tap((user) => {
          if (user && userUpdateRequest.language) {
            user.language = userUpdateRequest.language;
            this.authStore.setUser(user);
            this.authStore.setLanguage(user.language);
          }
        }),
      );
    }
    return this.httpClient.patch<any>('/api/me', UserUpdateRequest.mapToApiValue(userUpdateRequest)).pipe(
      tap((_user) => {
        const user = UserAccount.mapFromApiValue(_user);
        this.authStore.setUser(user);
        this.authStore.setLanguage(user.language);
      }),
    );
  }
}
